import { makeAutoObservable } from 'mobx'
import { isCancel } from 'axios'
import { RequestCancelAxios } from 'shared/api/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ConversationsAnalyticsApi } from 'entities/Analytics'
import { type ConversationsAnalyticsKeys } from 'entities/Analytics/api/types'
import { RowConversationsComparison } from 'entities/Analytics/model/Table/RowConversationsComparison'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { ConversationsChartDataStore } from 'pages/analytics/pages/conversations/ui/Chart'
import { AnalyticsFiltersStore } from 'pages/analytics/store/analyticsFiltersStore'
import { ExportAnalyticsModalStore } from 'pages/analytics/ui/ExportAnalyticsModal'
import {
  AnalyticsComparisonTableStore,
  type IAnalyticsComparisonTableConfig,
} from 'pages/analytics/store/analyticsComparisonTableStore'

export type ConversationsComparisonTableStore = AnalyticsComparisonTableStore<
  ConversationsAnalyticsKeys,
  RowConversationsComparison
>

export type IConversationsComparisonTableConfig = IAnalyticsComparisonTableConfig<
  ConversationsAnalyticsKeys,
  RowConversationsComparison
>

export class ConversationsAnalyticsPageStore {
  private readonly _chartDataStore: ConversationsChartDataStore
  private readonly _analyticsFiltersStore: AnalyticsFiltersStore
  private readonly _exportAnalyticsModalStore: ExportAnalyticsModalStore
  private readonly _comparisonTableStore: ConversationsComparisonTableStore
  private readonly _requestCancelAxios = new RequestCancelAxios()

  private _error: string | null = null
  private _pageLoading = false
  private _chartLoading = false

  constructor(entities: IEntitiesStore) {
    this._chartDataStore = new ConversationsChartDataStore()
    this._analyticsFiltersStore = new AnalyticsFiltersStore(entities, {
      onFiltersChange: (loadingType) => this._fetchAnalytics(loadingType),
      additionalFilterTypes: [
        SearchTypesEnum.members,
        SearchTypesEnum.teams,
        SearchTypesEnum.numbers,
        SearchTypesEnum.tags,
        SearchTypesEnum.time_range,
      ],
      breakdownFilterTypes: [
        SearchTypesEnum.members,
        SearchTypesEnum.teams,
        SearchTypesEnum.numbers,
        SearchTypesEnum.tags,
      ],
    })
    this._exportAnalyticsModalStore = new ExportAnalyticsModalStore(
      ConversationsAnalyticsApi.exportAnalytics
    )

    const comparisonTableConfig: IConversationsComparisonTableConfig = {
      tableKey: 'conversationsComparisonTable',
      getAnalytics: ConversationsAnalyticsApi.getAnalytics,
      createRow: (data) => new RowConversationsComparison(data),
      createMetricsRecord: () => ({
        open: 0,
        closed: 0,
        total: 0,
        new: 0,
      }),
    }

    this._comparisonTableStore = new AnalyticsComparisonTableStore(
      comparisonTableConfig,
      () => this._analyticsFiltersStore.analyticsFiltersParams,
      () => this._analyticsFiltersStore.breakdownFilterStore.appliedBreakdownFilterType,
      entities
    )

    makeAutoObservable(this)
  }

  init = () => {
    this._analyticsFiltersStore.initReactions()
    this._comparisonTableStore.init()
    this._fetchAnalytics('page')
  }

  openExportModal = () => {
    const { analyticsFiltersParams } = this._analyticsFiltersStore
    this._exportAnalyticsModalStore.onOpenExport(analyticsFiltersParams)
  }

  private _fetchAnalytics = async (loadingType: 'page' | 'chart' = 'page') => {
    if (loadingType === 'page') {
      this._pageLoading = true
    } else {
      this._chartLoading = true
    }

    try {
      this._error = null
      const { analyticsFiltersParams } = this._analyticsFiltersStore
      const { period } = this._analyticsFiltersStore.chartFilterStore

      this._requestCancelAxios.init()

      await ConversationsAnalyticsApi.preloadAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      const { data } = await ConversationsAnalyticsApi.getAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      this._chartDataStore.setRawData(data, period)
    } catch (error) {
      if (!isCancel(error)) {
        this._error = 'Failed to load analytics data'
      }
    } finally {
      if (loadingType === 'page') {
        this._pageLoading = false
      } else {
        this._chartLoading = false
      }
    }
  }

  get calendarFilterStore() {
    return this._analyticsFiltersStore.calendarFilterStore
  }

  get breakdownFilterStore() {
    return this._analyticsFiltersStore.breakdownFilterStore
  }

  get chartFilterStore() {
    return this._analyticsFiltersStore.chartFilterStore
  }

  get chartDataStore() {
    return this._chartDataStore
  }

  get comparisonTableStore() {
    return this._comparisonTableStore
  }

  get pageLoading() {
    return this._pageLoading
  }

  get chartLoading() {
    return this._chartLoading
  }

  get error() {
    return this._error
  }

  dispose = () => {
    this._analyticsFiltersStore.dispose()
    this._requestCancelAxios.reset()
    this._chartDataStore.dispose?.()
    this._comparisonTableStore.dispose()
    this._exportAnalyticsModalStore.dispose()
  }
}
